import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import { type SanityFrameBuilderDefaultFrame } from '../data/sanity/queries/types/default-frame'
import { getSanityImageUrl } from '../lib/image'
import {
  type FrameBuilderArtworkImage,
  type FrameBuilderExtraService,
  type FrameBuilderGlass,
  type FrameBuilderPassepartoutColor,
  type FrameBuilderPassepartoutSize,
  type FrameBuilderProfile,
  type FrameBuilderSpacerColor,
  type FrameBuilderSpacerSize,
  type FrameBuilderState,
  type FrameBuilderTexture,
  emptyFrameBuilderState,
} from '../lib/state'
import {
  defaultSectionSeparator,
  defaultSectionValueSeparator,
  fieldSeparator,
  getStateFromFrameString,
  valueSeparator,
} from '../lib/state-serialization'
import { FrameBuilderConfigurationContext } from './configuration-context'

interface FrameBuilderDefaultFrameProviderProps {
  defaultFrame: SanityFrameBuilderDefaultFrame
  profiles: FrameBuilderProfile[]
  textures: FrameBuilderTexture[]
  passepartoutSizes: FrameBuilderPassepartoutSize[]
  passepartoutColors: FrameBuilderPassepartoutColor[]
  glass: FrameBuilderGlass[]
  spacerSizes: FrameBuilderSpacerSize[]
  spacerColors: FrameBuilderSpacerColor[]
  extraServices: FrameBuilderExtraService[]
  showExtrasStep: boolean
  children: ReactNode
}

interface FrameBuilderDefaultFrameContextProps {
  isDefaultStateLoaded: boolean
  defaultState: FrameBuilderState
}

export const FrameBuilderDefaultFrameContext =
  createContext<FrameBuilderDefaultFrameContextProps>({
    isDefaultStateLoaded: false,
    defaultState: emptyFrameBuilderState,
  })

/**
 * Gets frame builder default state from default frame query results.
 */
const getFrameBuilderDefaultState = (
  defaultFrame: SanityFrameBuilderDefaultFrame,
  allProfiles: FrameBuilderProfile[],
  allTextures: FrameBuilderTexture[],
  allPassepartoutSizes: FrameBuilderPassepartoutSize[],
  allPassepartoutColors: FrameBuilderPassepartoutColor[],
  allGlass: FrameBuilderGlass[],
  allSpacerSizes: FrameBuilderSpacerSize[],
  allSpacerColors: FrameBuilderSpacerColor[],
  allExtraServices: FrameBuilderExtraService[],
  showExtrasStep: boolean,
): FrameBuilderState => {
  const imageUrl = getSanityImageUrl(defaultFrame.previewImage)
  const image: FrameBuilderArtworkImage | undefined = imageUrl
    ? {
        url: imageUrl,
        name: '',
      }
    : undefined

  const backgroundColor = defaultFrame.backgroundColor?.hex
  const predefinedBackgroundColors = defaultFrame.backgroundColorPresets?.map(
    (backgroundColorPreset) => backgroundColorPreset.hex,
  )

  const profile = allProfiles.find(
    (profile) => profile._id === defaultFrame.profile?._ref,
  )
  const texture = allTextures.find(
    (texture) => texture._id === defaultFrame.texture?._ref,
  )
  const passepartoutSize = allPassepartoutSizes.find(
    (passepartoutSize) =>
      passepartoutSize._id === defaultFrame.passepartoutSize?._ref,
  )
  const passepartoutColor = allPassepartoutColors.find(
    (passepartoutColor) =>
      passepartoutColor._id === defaultFrame.passepartoutColor?._ref,
  )
  const glass = allGlass.find((glass) => glass._id === defaultFrame.glass?._ref)
  const extraServices: FrameBuilderExtraService[] = allExtraServices.filter(
    (extraService) =>
      defaultFrame.extraServices
        ?.map((defaultFrameExtraService) => defaultFrameExtraService._ref)
        ?.includes(extraService._id),
  )
  const spacerSize = allSpacerSizes.find(
    (spacerSize) => spacerSize._id === defaultFrame.spacerSize?._ref,
  )
  const spacerColor = allSpacerColors.find(
    (spacerColor) => spacerColor._id === defaultFrame.spacerColor?._ref,
  )

  return {
    artwork: {
      width: defaultFrame.width,
      height: defaultFrame.height,
      image,
      backgroundColor: {
        current: backgroundColor,
        predefinedColors: predefinedBackgroundColors,
      },
    },
    profile,
    texture,
    passepartout: {
      size: passepartoutSize,
      color: passepartoutColor,
    },
    glass,
    extras: {
      active: showExtrasStep,
      extraServices,
      spacer: {
        size: spacerSize,
        color: spacerColor,
      },
    },
  }
}

export const FrameBuilderDefaultFrameProvider = ({
  defaultFrame,
  profiles,
  textures,
  passepartoutSizes,
  passepartoutColors,
  glass,
  spacerSizes,
  spacerColors,
  extraServices,
  showExtrasStep,
  children,
}: FrameBuilderDefaultFrameProviderProps) => {
  const configuration = useContext(FrameBuilderConfigurationContext)

  const [isDefaultStateLoaded, setIsDefaultStateLoaded] = useState(false)
  const [defaultState, setDefaultState] = useState(() =>
    getFrameBuilderDefaultState(
      defaultFrame,
      profiles,
      textures,
      passepartoutSizes,
      passepartoutColors,
      glass,
      spacerSizes,
      spacerColors,
      extraServices,
      showExtrasStep,
    ),
  )

  // Load default frame state from the URL
  useEffect(() => {
    if (isDefaultStateLoaded) {
      return
    }

    const frameString =
      typeof window !== 'undefined'
        ? window.location.hash?.replace(/^[#]{1}/g, '')
        : null

    if (frameString) {
      // Overwrite default state with saved state
      setDefaultState(
        getStateFromFrameString(
          frameString,
          configuration,
          defaultState,
          defaultSectionSeparator,
          defaultSectionValueSeparator,
          fieldSeparator,
          valueSeparator,
        ),
      )
    }

    setIsDefaultStateLoaded(true)
  }, [
    configuration,
    defaultFrame,
    defaultState,
    extraServices,
    glass,
    isDefaultStateLoaded,
    passepartoutColors,
    passepartoutSizes,
    profiles,
    showExtrasStep,
    spacerColors,
    spacerSizes,
    textures,
  ])

  return (
    <FrameBuilderDefaultFrameContext.Provider
      value={{
        isDefaultStateLoaded,
        defaultState,
      }}
    >
      {children}
    </FrameBuilderDefaultFrameContext.Provider>
  )
}
